import React, {
	Dispatch,
	ForwardedRef,
	forwardRef,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import SaveProfile from "./edit-quiz-modals/saveProfile";
import styles from "./editQuiz.module.css";
import { api as defaultApi, AssessmentUuids, VerityApi, VerityApiContext } from "../api/fetch";
import { useMobileLayout } from "../hooks";
import { ExemptedStudent, ThirdPartyPlatform, VerityInstituteSettings } from "../types/common";
import { isValidPlatform } from "./proctoring-options/proctoringOptionsGrid";
import { Box, Link } from "@mui/material";
import { AssessmentInfo, EnabledProctoringOptions } from "@verity/api-client/types";
import { CoreCheckbox, CoreModal, CorePaper, CoreSpinner, CoreStepper, coreToast, CoreToastContainer } from "../core";
import {
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringOptionProfileV2,
	Application,
	OptionRelatedInstituteSettings,
	ProctoringPlatforms,
	Extension,
} from "@verity/proctoring-option/types";
import {
	convertPartialOptionsToNonNullable,
	DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION,
	enforceConfiguration,
	equalProctoringOptions,
	getProctoringOptionDisabledDetails,
	isAnyOnValueEnabledForProctoringOption,
	isEnforcedProfile,
	isInstituteProfile,
	isOptionSupportedForPlatform,
	PROCTORING_OPTION_INFO,
} from "@verity/proctoring-option";
import {
	ConfigurationPageName,
	PROCTORING_OPTION_CATEGORIES,
	getOptionsInCategory,
	StandaloneConfigurationPageName,
} from "./proctoring-options/option-text";
import { shouldShowProctoringOption } from "./proctoring-options/configurationSummaryList";
import { VerityLogger } from "@yujacorp/verity-logger";
import { intIdToUuid } from "../api/uuid";
import {
	getLiveSessionErrors,
	convertLiveSessionsToPendingSessions,
	getLiveSessionsCancelledByUpdate,
	LiveSessionError,
	LiveSessionWithInvitations,
	PendingLiveSession,
	ProctorGroupInfo,
	ProctorInfo,
	saveLiveSessions,
} from "./liveHelper";
import { StandAloneExemptedStudentsContext } from "./editQuizContext";
import i18nPackage from "../i18n/i18n";
import { I18nextProvider, Trans } from "react-i18next";
import type { i18n } from "i18next";
import ModerateTheQuiz from "./edit-quiz-steps/accommodation-step";
import ConfirmationPage from "./edit-quiz-steps/confirmation-step";
import ProctorSettings from "./edit-quiz-steps/configuration-step";
import LiveSessionsStep from "./edit-quiz-steps/live-sessions-step";
import MethodStep from "./edit-quiz-steps/method-step";
import PlatformStep from "./edit-quiz-steps/platform-step";
import ThirdPartyStep from "./edit-quiz-steps/third-party-step";
import { getPlatformIdFromUrl } from "@verity/url-matcher";
import { dispatchProxyMessage } from "@verity/extension/proxy-message/external";
import { UnsavedAssessmentInfo } from "./editQuizModalWrapper";

type SaveParameters = {
	password?: string;
	additionalAssessmentInfo?: Partial<AssessmentInfo>;
};

type EditQuizPageProps = {
	quizName?: string;
	courseName?: string;
	url?: string;
	api?: VerityApi;
	quizInfo: UnsavedAssessmentInfo;
	initialProctoringAssessmentConfiguration?: Omit<
		Partial<ProctoringAssessmentConfigurationV2>,
		"proctoringOptionProfile"
	>;
	proctoringAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	setProctoringAssessmentConfiguration: (
		options: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
	) => void;
	initialProctoringOption?: Partial<ProctoringOption>;
	proctoringOption?: Partial<ProctoringOption>;
	setProctoringOption: (options: Partial<ProctoringOption>) => void;
	currentProfileId?: number;
	setCurrentProfileId: (id: number | undefined) => void;
	enforcedProfilesOnly: boolean;

	onBackEnabledChange?: (enabled: boolean) => void;
	onNextEnabledChange?: (enabled: boolean, tooltip?: string) => void;
	/** Called when the proctoring options can or cannot be saved */
	onSaveEnabledChange?: (enabled: boolean) => void;
	onClose?: (confirmedOption?: Partial<ProctoringOption>) => void;
	setIsFinalPage?: (value: boolean) => void;
	forceStartOnFirstPage?: boolean;
	/** Called right before the options are being saved */
	onStartSaveOptions?: (confirmedOptions?: Partial<ProctoringOption>) => void;
	/** Called when options have finished saving (`success = true`)
	 * or failed to save (`success = false`). A `message` may be set if `success` is `false`. */
	onSaveOptions: (
		success: boolean,
		messages?: string[],
		options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0],
	) => void;

	uploadedExemptedStudentsMap: Map<number, ExemptedStudent>;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	uploadedProfilesMap: Map<number, ProctoringOptionProfileV2>;
	setUploadedProfilesMap: React.Dispatch<React.SetStateAction<Map<number, ProctoringOptionProfileV2>>>;
	logger?: VerityLogger;

	/* Dictionary of references to external HTML elements to use for events (Non-React) */
	externalRefs?: Record<string, HTMLElement>;

	setStudentsLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
	defaultProctoringOption?: ProctoringOption | null;

	/* Wraps the child component (stepper) in a predefined component */

	step?: number;
	setStep?: Dispatch<SetStateAction<number>>;
	totalSteps?: { name: string; label: string }[];
};

export type EditQuizRef = {
	saveButtonHandler: (password?: string, additionalAssessmentInfo?: Partial<AssessmentInfo>) => Promise<boolean>;
	closeButtonHandler: () => void;
	saveAsProfile: () => void;
	saveAsProfileButtonHandler: () => void;
	nextButtonHandler: () => void;
	backButtonHandler: () => void;
	goToPage: (pageName: ConfigurationPageName) => void;
};

function SaveConflict({
	open,
	onClose,
	profile,
	hasInstituteProfilePermissions,
	applyProfile,
	applyOptions,
	updateAndApplyProfile,
}: {
	open: boolean;
	onClose: () => void;
	profile: ProctoringOptionProfileV2;
	hasInstituteProfilePermissions: boolean;
	applyProfile: () => void;
	applyOptions: () => void;
	updateAndApplyProfile: () => void;
}) {
	const canUpdateProfile = !isInstituteProfile(profile) || hasInstituteProfilePermissions;

	return (
		<CoreModal
			title={i18nPackage.t("editQuizPage.conflictChanges.header")}
			open={open}
			onClose={onClose}
			minWidth="min(70%, 700px)"
			widthFixed
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.conflictChanges.applyProfile"),
					color: "primary",
					onClick: applyProfile,
				},
				{
					text: i18nPackage.t("editQuizPage.conflictChanges.applyChanges"),
					color: "primary",
					onClick: applyOptions,
				},
				{
					text: i18nPackage.t("editQuizPage.conflictChanges.updateAndApply"),
					color: "primary",
					onClick: updateAndApplyProfile,
					hide: !canUpdateProfile,
				},
			]}
		>
			<p>{i18nPackage.t("editQuizPage.conflictChanges.title")}</p>
			<p>
				{canUpdateProfile
					? i18nPackage.t("editQuizPage.conflictChanges.2choicesDesc")
					: i18nPackage.t("editQuizPage.conflictChanges.3choicesDesc")}
			</p>
		</CoreModal>
	);
}

export function convertProctoringAssessmentConfiguration(
	config: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
	forceThirdParty = false,
	initialPlatformId?: number,
) {
	const thirdPartyAssessmentOption = config.thirdPartyAssessmentOption ?? (forceThirdParty ? {} : undefined);

	// If third party, but no platform -> use platformId passed or try to get platform id from url if there is a url
	if (thirdPartyAssessmentOption && !thirdPartyAssessmentOption.thirdPartyPlatformId) {
		thirdPartyAssessmentOption.thirdPartyPlatformId =
			initialPlatformId || getPlatformIdFromUrl(thirdPartyAssessmentOption.thirdPartyUrl ?? "");
	}

	return {
		...config,
		thirdPartyAssessmentOption,
	};
}

export function convertProctoringOption(option: Partial<ProctoringOption>): Partial<ProctoringOption> {
	return {
		...option,
		hideOtherWindows: option.isLockdown && option.hideOtherWindows,
	};
}

type DisclaimerModalProps = {
	open: boolean;
	themeExists: boolean;
	verifyingTheme: boolean;
	setFinishedPresave: (x: boolean) => void;
	setOpen: (x: boolean) => void;
	setCallReset: (x: boolean) => void;
};

/* Disclaimer modal regarding Canvas Theme */
const DisclaimerModal = ({ open, verifyingTheme, setFinishedPresave, setOpen, setCallReset }: DisclaimerModalProps) => {
	/* Canvas specific theme verification */
	//TODO: Replace with Generic Boxes when merged
	const [disclaimerRead, setDisclaimerRead] = useState<boolean>(false);

	const closeHandler = () => {
		setCallReset(true);
		setOpen(false);
	};

	const submitHandler = () => {
		setFinishedPresave(true);
		setOpen(false);
	};

	return (
		<>
			<CoreModal
				title={i18nPackage.t("editQuizPage.confirmationStep.disclaimerModal.title")}
				widthFixed
				minWidth="720px"
				padding={"20px"}
				open={open}
				onClose={closeHandler}
				buttons={[
					{
						text: i18nPackage.t("editQuizPage.confirmationStep.disclaimerModal.cancel"),
						color: "secondary",
						onClick: closeHandler,
					},
					{
						text: i18nPackage.t("editQuizPage.confirmationStep.disclaimerModal.continue"),
						color: "primary",
						onClick: submitHandler,
						disabled: !disclaimerRead || verifyingTheme,
					},
				]}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						fontSize: "15px",
						fontWeight: "400",
						color: "#000000",
					}}
				>
					{verifyingTheme ? (
						<CoreSpinner size={100} />
					) : (
						<>
							<Box sx={{ display: "flex" }}>
								<Trans
									i18nKey="editQuizPage.confirmationStep.disclaimerModal.bodyLink"
									components={{
										a: (
											<Link
												href="https://support.yuja.com/hc/en-us/articles/7332584857623-Integrating-YuJa-Verity-for-Test-Proctoring-into-Canvas#h_01HCJH9QWSFKKFBYSG3TX75XM1"
												target="_blank"
												rel="noreferrer"
											/>
										),
									}}
								/>
							</Box>

							<Box sx={{ display: "flex", height: "15px" }}></Box>

							<Box sx={{ display: "flex" }}>
								<div>{i18nPackage.t("editQuizPage.confirmationStep.disclaimerModal.bodyText")}</div>
							</Box>

							<Box sx={{ display: "flex", height: "15px" }}></Box>

							<Box sx={{ display: "flex", alignItems: "center" }}>
								<CoreCheckbox
									size={17}
									checked={disclaimerRead}
									onChange={(e) => {
										setDisclaimerRead(e.target.checked);
									}}
									style={{ padding: "0px" }}
								></CoreCheckbox>

								<Box sx={{ display: "flex", width: "7px" }}></Box>

								<div>{i18nPackage.t("editQuizPage.confirmationStep.disclaimerModal.checkboxText")}</div>
							</Box>
						</>
					)}
				</Box>
			</CoreModal>
		</>
	);
};

type UnsavedChangesModalProps = {
	openModal: boolean;
	onClose: () => void;
	onConfirm: () => void;
	inLms?: boolean;
};

export const UnsavedChangesModal = ({ openModal, onClose, onConfirm, inLms }: UnsavedChangesModalProps) => {
	const isMobileLayout = useMobileLayout();

	return (
		<CoreModal
			title={i18nPackage.t("editQuizPage.unsavedChanges.title")}
			open={openModal}
			minWidth={!isMobileLayout ? "min(600px, 80%)" : undefined}
			maxWidth={isMobileLayout ? "95%" : undefined}
			maxHeight="min(640px, 80%)"
			onClose={() => onClose()}
			equalButtons
			footerVerticalPadding={"10px"}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.unsavedChanges.cancelButton"),
					color: "secondary",
					onClick: () => onClose(),
				},
				{
					text: i18nPackage.t("editQuizPage.unsavedChanges.leaveButton"),
					onClick: () => onConfirm(),
				},
			]}
		>
			{inLms
				? i18nPackage.t("editQuizPage.unsavedChanges.lmsDescription")
				: i18nPackage.t("editQuizPage.unsavedChanges.ltiDescription")}
		</CoreModal>
	);
};

type NoConfigurationsEnabledModalProps = {
	openModal: boolean;
	setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	onClose: () => void;
	onConfirm: () => void;
};

const NoConfigurationsEnabledModal = ({
	openModal,
	setOpenModal,
	onClose,
	onConfirm,
}: NoConfigurationsEnabledModalProps) => {
	return (
		<CoreModal
			title={i18nPackage.t("editQuizPage.confirmationStep.noConfigurations.title")}
			open={openModal}
			widthFixed
			minWidth="min(625px, 80%)"
			maxHeight="min(640px, 80%)"
			onClose={() => {
				setOpenModal(false);
				onClose();
			}}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.confirmationStep.noConfigurations.cancel"),
					color: "secondary",
					onClick: () => {
						setOpenModal(false);
						onClose();
					},
				},
				{
					text: i18nPackage.t("editQuizPage.confirmationStep.noConfigurations.yes"),
					onClick: () => {
						setOpenModal(false);
						onConfirm();
					},
				},
			]}
		>
			<p>{i18nPackage.t("editQuizPage.confirmationStep.noConfigurations.description")}</p>
		</CoreModal>
	);
};

/* Checks if Canvas theme is applied. */
const CanvasThemeValidator = (setThemeExists: React.Dispatch<React.SetStateAction<boolean>>) => {
	if (window.chrome) {
		chrome.runtime
			.sendMessage(import.meta.env.VITE_EXTENSION_ID, { request: "checkThemeExists" })
			.then((res) => {
				setThemeExists(res.status);
			})
			.catch((e) => {
				console.error(e);
				setThemeExists(false);
			});
	} else {
		dispatchProxyMessage({ request: "checkThemeExists" })
			.then((res: { status: boolean }) => {
				setThemeExists(res.status);
			})
			.catch((e) => {
				console.error(e);
				setThemeExists(false);
			});
	}

	return;
};

export const EditQuizPage = forwardRef(function EditQuizPage(
	{
		quizName,
		courseName,
		url,
		api = defaultApi,
		quizInfo,
		initialProctoringAssessmentConfiguration,
		proctoringAssessmentConfiguration,
		setProctoringAssessmentConfiguration,
		initialProctoringOption,
		proctoringOption,
		setProctoringOption,
		currentProfileId,
		setCurrentProfileId,
		enforcedProfilesOnly,
		onBackEnabledChange,
		onNextEnabledChange,
		onSaveEnabledChange,
		onClose,
		setIsFinalPage,
		forceStartOnFirstPage = false,
		onStartSaveOptions,
		onSaveOptions,
		uploadedExemptedStudentsMap,
		setUploadedExemptedStudentsMap,
		uploadedProfilesMap,
		setUploadedProfilesMap,
		logger,
		defaultProctoringOption,
		step,
		setStep,
		totalSteps,
	}: EditQuizPageProps,
	ref: ForwardedRef<EditQuizRef>,
) {
	const {
		saveProctoringAssessmentConfiguration,
		getVerityInstituteSettings,
		getEnabledProctoringOptions,
		getProctoringProfiles,
		getProctoringExemptionStudents,
		doesUserHaveInstituteProfilePermissions,
		updateProctoringProfile,
		getAllApplications,
		getAllExtensions,
		fetchChromeExtensionData,
		getCourseStudents,
		saveProctoringExemptionStudents,
		getUserInstituteId,
		getCourseById,
		getCourseByLmsCourseId,
		getAssessmentByAssessmentInfo,
		getInstitute,
		getCourseInstructors,
		getInstituteProctorGroups,
		getAssessmentLiveSessions,
		getLiveSessionInvitations,
		getInstituteSettings,
		getThirdPartyPlatforms,
	} = api;

	const optionsSaved = (
		success: boolean,
		options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0],
		message?: string,
	) => {
		onSaveOptions(success, message ? [message] : undefined, options);
		void logger?.assessment.edit.configurationModal.saveFinished(success, message);
	};

	const isMobileLayout = useMobileLayout();

	const [exemptionCount, setExemptionCount] = useState<number>(0);

	const [profiles, setProfiles] = useState<Array<ProctoringOptionProfileV2>>([]);
	const [activeProfile, setActiveProfile] = useState<ProctoringOptionProfileV2>();

	const [verityInstituteSettings, setVerityInstituteSettings] = useState<VerityInstituteSettings>();
	const [optionRelatedInstituteSettings, setOptionRelatedInstituteSettings] =
		useState<OptionRelatedInstituteSettings>();
	const [enabledProctoringOptions, setEnabledProctoringOptions] = useState<EnabledProctoringOptions>({
		proctoringMethod: ["automated", "live", "pop-in", "pop-in-post-review"],
	});
	const [hasInstituteProfilePermissions, setHasInstituteProfilePermissions] = useState(false);
	const [thirdPartySaveEnabled, setThirdPartySaveEnabled] = useState(true);
	const [saveProfileModalShowing, setSaveProfileModalShowing] = useState(false);
	const [saveConflictModalShowing, setSaveConflictModalShowing] = useState(false);
	const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
	const [noConfigurationsEnabledModalOpen, setNoConfigurationsEnabledModalOpen] = useState(false);
	const [confirmSaveModalResolve, setConfirmSaveModalResolve] = useState<((value: boolean) => void) | null>(null);
	const [confirmCloseModalResolve, setConfirmCloseModalResolve] = useState<((value: boolean) => void) | null>(null);
	const [previousProfileOptions, setPreviousProfileOptions] = useState<Partial<ProctoringOption> | undefined>(
		proctoringOption,
	);

	const assessmentUuids = useRef<
		| (Omit<AssessmentUuids, "assessmentUuid"> & {
				assessmentUuid?: string;
		  })
		| undefined
	>();
	const [existingLiveSessions, setExistingLiveSessions] = useState<Record<string, LiveSessionWithInvitations>>({});
	const [availableProctors, setAvailableProctors] = useState<Record<string, ProctorInfo>>({});
	const [availableProctorGroups, setAvailableProctorGroups] = useState<Record<number, ProctorGroupInfo>>({});

	const [loading, setLoading] = useState(false);

	const [password, setPassword] = useState<string>();
	const [additionalAssessmentInfo, setAdditionalAssessmentInfo] = useState<Partial<AssessmentInfo>>();
	const [isUpdateCurrentProfileClicked, setIsUpdateCurrentProfileClicked] = useState(false); // true when user wants to update the current profile
	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);
	const [thirdPartyPlatforms, setThirdPartyPlatforms] = useState<ThirdPartyPlatform[]>([]);

	/* Stateful variable to call the saving handler */
	const [saveRequested, setSaveRequested] = useState<boolean>(false);
	const [finishedPresave, setFinishedPresave] = useState<boolean>(false);
	const [saveParameters, setSaveParameters] = useState<SaveParameters>({
		password: undefined,
		additionalAssessmentInfo: undefined,
	});

	/* Page can be called asynchronously, use state to determine if background steps have been completed */
	const [backgroundStepsComplete, setBackgroundStepsComplete] = useState<boolean>(false);

	/* Canvas Theme Disclaimer */
	const [openDisclaimer, setOpenDisclaimer] = useState<boolean>(false);
	const [themeExists, setThemeExists] = useState<boolean>(false);
	const [verifyingTheme, setVerifyingTheme] = useState<boolean>(false);

	/* Pass in a promise we resolve/reject upon the user accepting the disclaimer */
	const [savePromise, setSavePromise] = useState<PromiseWithResolvers<void>>(Promise.withResolvers<void>());

	/* Saving reset function & UseEffect wrapper for component calls*/
	const [callReset, setCallReset] = useState<boolean>(false);

	const resetSaveStates = useCallback(() => {
		try {
			savePromise.promise.catch((e) => console.error(e, "Resetting Promise"));
			savePromise.reject();
		} catch (e) {
			console.error(e, "Resetting Promise");
		}

		setSaveRequested(false);
		setFinishedPresave(false);
		setSavePromise(Promise.withResolvers<void>());
		setSaveParameters({
			password: undefined,
			additionalAssessmentInfo: undefined,
		});
	}, [savePromise, setSaveRequested, setFinishedPresave, setSavePromise, setSaveParameters]);

	const currFakeLiveSessionId = useRef(0);
	const [pendingLiveSessionIds, setPendingLiveSessionIds] = useState<string[]>([]);
	const [pendingLiveSessions, setPendingLiveSessions] = useState<Record<string, PendingLiveSession>>({});

	const getNextFakeLiveSessionId = () => `#${currFakeLiveSessionId.current++}`;

	const fetchAndSetExistingLiveSessions = async (): Promise<LiveSessionWithInvitations[]> => {
		if (!assessmentUuids.current) {
			throw new Error("no assessmentUuids were obtained");
		}
		if (!quizInfo.courseId) {
			throw new Error("no course ID");
		}

		const instructors = await getCourseInstructors(quizInfo.courseId);
		setAvailableProctors(Object.fromEntries(instructors.map(({ uuid, name }) => [uuid, { id: uuid, name }])));

		setAvailableProctorGroups(
			Object.fromEntries(
				(await getInstituteProctorGroups(assessmentUuids.current.instituteUuid)).results.map((proctorGroup) => [
					proctorGroup.id,
					{ ...proctorGroup, autoCancelEmptyHoursBefore: proctorGroup.autoCancelEmptyHoursBefore ?? undefined },
				]),
			),
		);

		const sessions = assessmentUuids.current.assessmentUuid
			? (
					await getAssessmentLiveSessions({
						...assessmentUuids.current,
						assessmentUuid: assessmentUuids.current.assessmentUuid,
					})
				).results
			: [];
		const invitations = Object.fromEntries(
			(
				await Promise.allSettled(
					sessions.map(async ({ id }) => [
						id,
						(await getLiveSessionInvitations(id)).filter(({ state }) => !state.cancelledAt),
					]),
				)
			)
				.filter((result) => result.status === "fulfilled")
				.map(
					(result) =>
						(
							result as PromiseFulfilledResult<
								[
									string,
									{
										liveSessionId: string;
										proctorGroupId: number;
									}[],
								]
							>
						).value,
				),
		);
		const sessionsWithInvitations = sessions.map((session) => ({
			...session,
			invitations: invitations[session.id] ?? [],
		}));
		setExistingLiveSessions(Object.fromEntries(sessionsWithInvitations.map((session) => [session.id, session])));
		return sessionsWithInvitations;
	};

	const fetchAndSetLiveSessions = async () => {
		const sessions = await fetchAndSetExistingLiveSessions();
		setPendingLiveSessionIds(sessions.map(({ id }) => id));
		setPendingLiveSessions(convertLiveSessionsToPendingSessions(sessions));
	};

	const [studentsLoaded, setStudentsLoaded] = useState(false);

	const initialExemptedStudentsMapRef = useRef<Map<number, ExemptedStudent> | null>(null);

	useEffect(() => {
		if (!(uploadedExemptedStudentsMap === null)) {
			if (initialExemptedStudentsMapRef.current === null && studentsLoaded) {
				initialExemptedStudentsMapRef.current = new Map(uploadedExemptedStudentsMap);
			}
		}
	}, [uploadedExemptedStudentsMap]);

	useEffect(() => {
		if (callReset) {
			resetSaveStates();
			setCallReset(false);
		}
	}, [callReset]);

	/* On Load & Valid Configuration, perform LMS relevant background steps */
	useEffect(() => {
		/* No need to run if background steps have already been completed */
		if (backgroundStepsComplete) {
			return;
		}

		/* Handle according to LMS */
		if (quizInfo.lms === "canvas") {
			/* Page has not recieved configuration */
			if (!proctoringAssessmentConfiguration) {
				return;
			}

			/* Check if quiz is third party */
			if (!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption && !isStandaloneQuizFlow) {
				/* If not, verify if theme exists */
				setVerifyingTheme(true);
				CanvasThemeValidator(setThemeExists);
				setVerifyingTheme(false);
			}

			setBackgroundStepsComplete(true);
		} else {
			/* Implement other LMS background steps if required via additional conditions */
			setBackgroundStepsComplete(true);
		}
	}, [proctoringAssessmentConfiguration]);

	useEffect(() => {
		if (currentProfileId !== activeProfile?.id) {
			const foundProfile = currentProfileId ? profiles.find((x) => x.id === currentProfileId) : undefined;
			if (foundProfile && (!enforcedProfilesOnly || isEnforcedProfile(foundProfile))) {
				setActiveProfile(foundProfile);
			} else {
				setActiveProfile(undefined);
			}
		}
	}, [profiles, currentProfileId, enforcedProfilesOnly, activeProfile]);

	/* Save Request Routing */
	useEffect(() => {
		/* If save has been requested */
		if (saveRequested) {
			/* Initiate save if presave steps are complete */
			if (finishedPresave) {
				void defaultSave();
			} else {
				/* Otherwise, initiate presave steps */
				switch (quizInfo.lms) {
					case "canvas":
						if (
							!!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption ||
							isStandaloneQuizFlow ||
							themeExists
						) {
							setFinishedPresave(true);
						} else {
							setOpenDisclaimer(true);
						}
						break;
					case "moodle":
						setFinishedPresave(true);
						break;
					case "BlackboardLearn":
						setFinishedPresave(true);
						break;
					case "desire2learn":
						setFinishedPresave(true);
						break;
					default:
						setFinishedPresave(true);
						break;
				}
			}
		}
	}, [saveRequested, finishedPresave]);

	const addAssessmentId = async ({
		instituteUuid,
		courseUuid,
		assessmentUuid,
	}: Omit<AssessmentUuids, "assessmentUuid"> & {
		assessmentUuid?: string;
	}): Promise<AssessmentUuids | null> => {
		let newAssessmentUuid: string;
		const quizId = quizInfo.quizId;
		if (!quizId) {
			return null;
		}

		if (assessmentUuid) {
			newAssessmentUuid = assessmentUuid;
		} else {
			const assessment = await getAssessmentByAssessmentInfo({
				...quizInfo,
				quizId: quizId,
			});
			if (!assessment) {
				throw new Error("Assessment not found");
			}
			newAssessmentUuid = intIdToUuid(assessment.id);
			assessmentUuids.current = { instituteUuid, courseUuid, assessmentUuid: newAssessmentUuid };
		}
		return { instituteUuid, courseUuid, assessmentUuid: newAssessmentUuid };
	};

	const saveLiveSessionsHandleErrors = async () => {
		if (assessmentUuids.current) {
			try {
				const assessmentUuidsResult = await addAssessmentId(assessmentUuids.current);
				if (!assessmentUuidsResult) {
					throw new Error("No assessment UUIDs");
				}
				await saveLiveSessions({
					api,
					assessmentUuids: assessmentUuidsResult,
					existingSessions: existingLiveSessions,
					pendingSessions: pendingLiveSessions,
				});
			} finally {
				// update existing sessions for diff calculation
				await fetchAndSetExistingLiveSessions();
			}
		} else if (pendingLiveSessionIds.length > 0) {
			throw new Error("No assessment UUIDs");
		}
	};

	const validateLiveSession = useCallback(() => {
		const liveSessionErrors = getLiveSessionErrors(existingLiveSessions, pendingLiveSessions, availableProctorGroups);
		const cancelledLiveSessions = getLiveSessionsCancelledByUpdate(
			existingLiveSessions,
			pendingLiveSessions,
			availableProctorGroups,
		);
		if (Object.values(liveSessionErrors).some((errors) => errors.length > 0)) {
			const sessionNamesList = Object.entries(liveSessionErrors)
				.filter(([, errors]) => errors.length > 0)
				.map(([id]) => pendingLiveSessions[id]?.name || "[Unnamed]");
			let sessionNames: string;
			if (sessionNamesList.length === 1) {
				[sessionNames] = sessionNamesList;
			} else {
				sessionNames = `${sessionNamesList.slice(0, -1).join(", ")} and ${sessionNamesList[sessionNamesList.length - 1]}`;
			}
			coreToast.error(
				Object.values(liveSessionErrors).some((errors) => errors.includes("noSessions"))
					? i18nPackage.t("editQuizPage.liveSessionsStep.liveSession.error.noLiveSessionMessage")
					: i18nPackage.t("editQuizPage.liveSessionsStep.liveSession.error.sessionIssueMessage", { sessionNames }),
				{ containerId: "editQuiz" },
			);
			return false;
		}
		if (cancelledLiveSessions.length > 0) {
			const cancelledSessionNames = cancelledLiveSessions.map(({ name }) => name).join(", ");
			setLiveSessionsNextConfirmationMessage(
				i18nPackage.t("editQuizPage.liveSessionsStep.liveSession.error.sessionWarningMessage", {
					cancelledSessionNames,
				}),
			);
		}
		return true;
	}, [existingLiveSessions, pendingLiveSessions, availableProctorGroups]);

	const defaultSave = async () => {
		const currentQuizId = quizInfo.quizId;

		if (!currentQuizId) {
			onStartSaveOptions?.(proctoringOption);
			onSaveOptions(true, undefined);
			return;
		}

		/* Define the functions error handler */
		const logError = (e: unknown) => {
			console.error("Failed to save verity option ", e);
			void logger?.error(e);
			if (e instanceof LiveSessionError) {
				onSaveOptions(false, e.messages);
			} else {
				onSaveOptions(false, [i18nPackage.t("editQuizPage.liveSessionsStep.liveSession.error.applyProfileFailed")]);
			}
			resetSaveStates();
		};

		/* Destructure passed parameters */
		const { password, additionalAssessmentInfo } = saveParameters;

		// if options are different from profile, prompt choice between option or profile
		if (
			proctoringOption &&
			activeProfile &&
			!equalProctoringOptions(convertPartialOptionsToNonNullable(proctoringOption), activeProfile.proctoringOption)
		) {
			if (!isUpdateCurrentProfileClicked) {
				setCurrentProfileId(undefined);
			}
			try {
				const savedOptions = await saveProctoringConfiguration(
					password,
					additionalAssessmentInfo,
					false,
					!isUpdateCurrentProfileClicked ? true : false,
				);
				await saveLiveSessionsHandleErrors();
				void logger?.assessment.edit.configurationModal.saveFinished(true);
				onSaveOptions(true, undefined, savedOptions);
			} catch (e) {
				logError(e);
			}
			setPassword(password);
			setAdditionalAssessmentInfo(additionalAssessmentInfo);
		} else {
			try {
				const savedOptions = await saveProctoringConfiguration(password, additionalAssessmentInfo);
				if (!standAloneExemptedStudentsContext.isCreating) {
					// only save the exempted students here if we're not creating a standalone assessment
					// (there is a seperate workflow to add exempted students during standalone creation)
					await saveProctoringExemptionStudents({
						assessmentInfo: { courseId: quizInfo?.courseId, quizId: currentQuizId },
						students: Array.from(uploadedExemptedStudentsMap.values()).filter(
							(x) => !x.proctoringOptionProfileId || uploadedProfilesMap.has(x.proctoringOptionProfileId),
						),
					});
				}
				await saveLiveSessionsHandleErrors();
				void logger?.assessment.edit.configurationModal.saveFinished(true);
				onSaveOptions(true, undefined, savedOptions);
			} catch (e) {
				logError(e);
			}
		}

		/* Resolve the promise passed back to parents */
		savePromise.resolve();
	};

	const confirmSave = useCallback(() => {
		return new Promise<boolean>((resolve) => {
			setNoConfigurationsEnabledModalOpen(true);
			void logger?.assessment.edit.configurationModal.warningShown("No Configurations Enabled");
			setConfirmSaveModalResolve(() => resolve);
		});
	}, [setNoConfigurationsEnabledModalOpen, logger, setConfirmSaveModalResolve]);

	const confirmClose = useCallback(() => {
		return new Promise<boolean>((resolve) => {
			setUnsavedChangesModalOpen(true);
			void logger?.assessment.edit.configurationModal.warningShown("Unsaved Changes");
			setConfirmCloseModalResolve(() => resolve);
		});
	}, [setUnsavedChangesModalOpen, logger, setConfirmCloseModalResolve]);

	/**
	 * This function is awaited by 2 parent wrappers on clicking submit
	 * The parents upon click of submit also execute other internal logic
	 *
	 * Hence, we must perform any logic that is to be executed on save request
	 * while halting the parent call
	 *
	 * Manually create and return a promise to the parent on call, and
	 * resolve / reject upon completion
	 *
	 */
	const saveButtonHandler: EditQuizRef["saveButtonHandler"] = useCallback(
		async (password, additionalAssessmentInfo) => {
			// Since progress bar is non linear, it is possible for user to create a live session, click back button (skipping the validation), then jump to confirmation step.
			// Intentionally did not add validation check to back button because if user wants to switch method to automated in step 1,
			// they must delete a live session in step 2, but if validation blocks them from leaving step 2 cuz they didn't create a live session, it creates an infinite loop
			if (proctoringAssessmentConfiguration?.isLiveProctored && !validateLiveSession()) return false;

			const areProctoringOptionsSet = () => {
				// Keys for DEFAULT_PROCTORING_OPTIONs that are not toggleable restrictions (isExtension, isLockdown, etc.)
				const proctoringOptionFilteredKeys = [
					"showRecordingPanel",
					"enforceLockdown",
					"enforceMobile",
					"isExtension",
					"isLockdown",
					"isMobile",
					"isChromeOs",
				];

				if (proctoringOption && defaultProctoringOption) {
					const proctoringOptionEntries = Object.entries(proctoringOption).sort();
					const defaultProctoringOptionEntries = Object.entries(defaultProctoringOption).sort();

					for (let i = 0; i < proctoringOptionEntries.length; i++) {
						const [key, value] = proctoringOptionEntries[i];
						const defaultValue = defaultProctoringOptionEntries[i]?.[1];

						if (proctoringOptionFilteredKeys.includes(key)) {
							continue;
						}

						if (key === "applicationBlockList") {
							if (proctoringOption.applicationBlockMode === "off") continue;
						} else if (key === "excludedWebTrafficRecordingUrls") {
							if (!proctoringOption.recordWebTraffic) continue;
						} else if (key === "keyboardShortcutBlockList") {
							if (!proctoringOption.recordInput) continue;
						}

						if (
							((Array.isArray(value) && value.length == 0) || value === null) &&
							((Array.isArray(defaultValue) && defaultValue.length == 0) || defaultValue === null)
						) {
							continue;
						}

						if (value !== defaultValue) {
							return true;
						}
					}
				}
				return false;
			};

			if (proctoringOption && defaultProctoringOption) {
				if (!areProctoringOptionsSet()) {
					const userConfirmed = await confirmSave();
					if (!userConfirmed) {
						return false;
					}
				}
			}
			/**
			 * We enter the function body on execution of the parent saveHandler.
			 * Hence, discard previous Save States and initialize new one
			 */
			resetSaveStates();
			const requestPromise = Promise.withResolvers<void>();
			setFinishedPresave(false);
			setSavePromise(requestPromise);
			setSaveParameters({ password: password, additionalAssessmentInfo: additionalAssessmentInfo });
			setSaveRequested(true);
			return true;
		},
		[
			validateLiveSession,
			proctoringAssessmentConfiguration?.isLiveProctored,
			proctoringOption,
			defaultProctoringOption,
			resetSaveStates,
			confirmSave,
		],
	);

	const closeButtonHandler: EditQuizRef["closeButtonHandler"] = useCallback(async () => {
		const hasAssessmentConfigurationChanged = () => {
			if (initialProctoringAssessmentConfiguration && proctoringAssessmentConfiguration) {
				const initialProctoringAssessmentConfigurationEntries = Object.entries(
					initialProctoringAssessmentConfiguration,
				).sort();
				const proctoringAssessmentConfigurationRefEntries = Object.entries(proctoringAssessmentConfiguration).sort();

				for (let i = 0; i < initialProctoringAssessmentConfigurationEntries.length; i++) {
					const [key, value] = initialProctoringAssessmentConfigurationEntries[i];
					const currentValue = proctoringAssessmentConfigurationRefEntries[i]?.[1];

					if (key === "password" || key === "enabled") {
						continue;
					}

					if (key === "thirdPartyAssessmentOption") {
						if (value && currentValue) {
							const initialThirdPartyEntries = Object.entries(value).sort();
							const thirdPartyEntries = Object.entries(currentValue).sort();

							for (let i = 0; i < initialThirdPartyEntries.length; i++) {
								const [thirdPartyKey, thirdPartyValue] = initialThirdPartyEntries[i];
								const thirdPartyCurrentValue = thirdPartyEntries[i]?.[1];

								if (thirdPartyKey === "password") {
									if (
										(thirdPartyValue === null || thirdPartyValue === "") &&
										(thirdPartyCurrentValue === null || thirdPartyCurrentValue === "")
									) {
										continue;
									}
								}

								if (thirdPartyValue !== thirdPartyCurrentValue) {
									return true;
								}
							}
						}
						continue;
					}

					if (value !== currentValue) {
						return true;
					}
				}
			}
			return false;
		};

		const hasProctoringOptionChanged = () => {
			if (initialProctoringOption && proctoringOption) {
				const initialProctoringOptionEntries = Object.entries(initialProctoringOption).sort();
				const proctoringOptionEntries = Object.entries(proctoringOption).sort();

				for (let i = 0; i < initialProctoringOptionEntries.length; i++) {
					const [key, value] = initialProctoringOptionEntries[i];
					const currentValue = proctoringOptionEntries[i]?.[1];

					if (key === "showRecordingPanel") {
						//showRecordingPanel is set from false (default) to true without user input when they access the 'Method' tab
						continue;
					}

					if (key === "applicationBlockList") {
						if (proctoringOption.applicationBlockMode === "off") continue;
					} else if (key === "excludedWebTrafficRecordingUrls") {
						if (!proctoringOption.recordWebTraffic) continue;
					} else if (key === "keyboardShortcutBlockList") {
						if (!proctoringOption.recordInput) continue;
					}

					if (Array.isArray(value) && Array.isArray(currentValue)) {
						if (JSON.stringify(value.sort()) !== JSON.stringify(currentValue.sort())) {
							return true;
						}
					} else if (value !== currentValue) {
						return true;
					}
				}
			}
			return false;
		};

		const hasExemptedStudentsMapChanged = () => {
			if (initialExemptedStudentsMapRef.current && uploadedExemptedStudentsMap) {
				if (initialExemptedStudentsMapRef.current.size !== uploadedExemptedStudentsMap.size) {
					return true;
				}
				for (const [key, value] of initialExemptedStudentsMapRef.current) {
					const uploadedValue = uploadedExemptedStudentsMap?.get(key);
					if (!uploadedValue || JSON.stringify(uploadedValue) !== JSON.stringify(value)) {
						return true;
					}
				}
			}
			return false;
		};

		if (hasAssessmentConfigurationChanged() || hasProctoringOptionChanged() || hasExemptedStudentsMapChanged()) {
			const userConfirmed = await confirmClose();
			if (!userConfirmed) {
				return;
			}
		}

		if (onClose) {
			onClose();
		}
	}, [
		initialProctoringAssessmentConfiguration,
		proctoringAssessmentConfiguration,
		initialProctoringOption,
		proctoringOption,
		uploadedExemptedStudentsMap,
		confirmClose,
		onClose,
	]);

	async function updateProfile() {
		if (activeProfile?.id && proctoringOption) {
			await updateProctoringProfile({
				id: activeProfile.id,
				proctoringOption: proctoringOption,
			});
		}
	}

	async function saveProctoringConfiguration(
		password?: string,
		additionalAssessmentInfo?: Partial<AssessmentInfo>,
		onlyProfile?: boolean,
		onlyOptions?: boolean,
	) {
		const quizId = quizInfo.quizId;
		if (!quizId) {
			throw new Error("Cannot save configuration without a Quiz ID");
		}

		const options = {
			assessmentInfo: {
				...quizInfo,
				...(additionalAssessmentInfo ?? {}),
				quizId: quizId,
			},
			profileId: onlyOptions ? undefined : activeProfile?.id,
			proctoringAssessmentConfiguration: {
				...proctoringAssessmentConfiguration,
				...(password ? { password } : {}),
			},
			proctoringOption: onlyProfile ? undefined : proctoringOption,
		};

		void logger?.assessment.edit.configurationModal.saveStarted({ ...options, onlyProfile, onlyOptions });
		await saveProctoringAssessmentConfiguration(options);
		return options;
	}

	const saveAsProfileButtonHandler: EditQuizRef["saveAsProfileButtonHandler"] = () => {
		setSaveProfileModalShowing(true);
	};

	const saveAsProfile: EditQuizRef["saveAsProfile"] = () => {
		saveAsProfileButtonHandler();
	};

	const isProctoringOptionAllowed = useCallback(
		<Option extends keyof EnabledProctoringOptions>(option: Option, value: EnabledProctoringOptions[Option][number]) =>
			enabledProctoringOptions[option].includes(value),
		[enabledProctoringOptions],
	);

	const getLiveSessionsStepIndex = () => {
		return fixedPageNames.indexOf("Method") + 1;
	};

	useEffect(() => {
		let proctoringMethod: EnabledProctoringOptions["proctoringMethod"][number];
		if (proctoringAssessmentConfiguration?.isLiveProctored) {
			proctoringMethod = "live";
		} else if (proctoringAssessmentConfiguration?.isLivePopIn) {
			if (proctoringAssessmentConfiguration.enablePostReview) {
				proctoringMethod = "pop-in-post-review";
			} else {
				proctoringMethod = "pop-in";
			}
		} else {
			proctoringMethod = "automated";
		}
		if (!isProctoringOptionAllowed("proctoringMethod", proctoringMethod)) {
			const allowedProctoringMethod = enabledProctoringOptions.proctoringMethod[0];
			switch (allowedProctoringMethod) {
				case "automated":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: false,
						enablePostReview: false,
					});
					break;
				case "live": {
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: true,
						isLivePopIn: false,
						enablePostReview: false,
					});
					const liveSessionsStepIndex = getLiveSessionsStepIndex();
					if (curPageValue > liveSessionsStepIndex) {
						setCurPageValue(liveSessionsStepIndex);
					}
					break;
				}
				case "pop-in":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: true,
						enablePostReview: false,
					});
					break;
				case "pop-in-post-review":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: true,
						enablePostReview: true,
					});
					break;
			}
		}
	}, [proctoringAssessmentConfiguration, isProctoringOptionAllowed]);

	const fixedPageNames = useMemo(() => {
		let pageNames: ConfigurationPageName[] = [];

		if (proctoringAssessmentConfiguration?.thirdPartyAssessmentOption) {
			pageNames.push("Third-Party");
		}
		if (!verityInstituteSettings?.liteMode) {
			pageNames.push("Method");
		}
		pageNames = pageNames.concat(["Platform", "Configuration", "Accommodations", "Confirmation"]);
		return pageNames;
	}, [verityInstituteSettings, !!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption]);

	const totalPageNames = useMemo(() => {
		const pages = [...fixedPageNames];
		if (proctoringAssessmentConfiguration?.isLiveProctored) {
			pages.splice(getLiveSessionsStepIndex(), 0, "Live Sessions");
		}
		const totalStepNames = totalSteps?.map((step) => step.name);
		return totalSteps ? (totalStepNames as (ConfigurationPageName | StandaloneConfigurationPageName)[]) : pages;
	}, [fixedPageNames, proctoringAssessmentConfiguration?.isLiveProctored]);

	useEffect(() => {
		/**
		 * If we detect an error in the third-party assessment configuration, force page to "Third-Party" until
		 * logic executes
		 */
		if (proctoringAssessmentConfiguration && proctoringAssessmentConfiguration.thirdPartyAssessmentOption) {
			const platformId = proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyPlatformId;
			const isPlatformAllowed = thirdPartyPlatforms.some((platform) => platform.platformId === platformId);
			if (forceStartOnFirstPage || !platformId || !isPlatformAllowed) {
				setCurPageValue(totalPageNames.indexOf("Third-Party"));
				return;
			}
		}

		if (!forceStartOnFirstPage && initialProctoringAssessmentConfiguration?.enabled) {
			setCurPageValue(totalPageNames.indexOf("Confirmation"));
			setCompletedStepsSet(new Set(totalPageNames));
			return;
		}
	}, [
		forceStartOnFirstPage,
		initialProctoringAssessmentConfiguration,
		fixedPageNames,
		!!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption,
		thirdPartyPlatforms,
	]);

	const shouldShowOption = useCallback(
		(option: keyof ProctoringOption) =>
			shouldShowProctoringOption(
				option,
				proctoringOption,
				verityInstituteSettings,
				optionRelatedInstituteSettings,
				enabledProctoringOptions,
			),
		[proctoringOption, verityInstituteSettings, optionRelatedInstituteSettings, enabledProctoringOptions],
	);
	const isOptionAllowed = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]) =>
			value === undefined
				? isAnyOnValueEnabledForProctoringOption(
						option,
						proctoringOption,
						verityInstituteSettings,
						optionRelatedInstituteSettings,
						enabledProctoringOptions,
					)
				: !getProctoringOptionDisabledDetails(
						option,
						value,
						proctoringOption,
						verityInstituteSettings,
						optionRelatedInstituteSettings,
						enabledProctoringOptions,
					).disabled,
		[proctoringOption, verityInstituteSettings, optionRelatedInstituteSettings, enabledProctoringOptions],
	);

	// Given an option, returns the list of selected platforms that are supported for that option.
	// If the option is supported for all selected platforms, returns an empty set.
	const getPartiallySupportedPlatforms = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]): Set<keyof ProctoringPlatforms> => {
			const optionInfo = PROCTORING_OPTION_INFO[option];

			const valuesToCheck =
				value !== undefined
					? [value]
					: optionInfo?.isList
						? [[""] as ProctoringOption[O]]
						: (optionInfo?.onValues ?? []);

			const supportedPlatforms = new Set<keyof ProctoringPlatforms>();
			if (!proctoringOption) return new Set();
			const selectedPlatforms = (["isExtension", "isLockdown", "isMobile"] as (keyof ProctoringPlatforms)[]).filter(
				(platform) => proctoringOption[platform],
			);

			selectedPlatforms.forEach((platform) => {
				const isSupported = valuesToCheck.some((v) => isOptionSupportedForPlatform(platform, option, v));
				if (isSupported) {
					supportedPlatforms.add(platform);
				}
			});

			// If all selected platforms are supported, return empty
			if (supportedPlatforms.size === selectedPlatforms.length) return new Set();
			return new Set(supportedPlatforms);
		},
		[proctoringOption],
	);

	const allowedCategories = PROCTORING_OPTION_CATEGORIES.filter((category) =>
		getOptionsInCategory(category).some(shouldShowOption),
	);
	const totalCategories = allowedCategories.length;

	const [curPage, setCurPage] = useState<number>(0);
	const [completedStepsSet, setCompletedStepsSet] = useState<
		Set<ConfigurationPageName | StandaloneConfigurationPageName>
	>(new Set()); // Using page name instead of index because of dynamic steps (aka the live session step makes the steps dynamic)

	const curPageValue = step ?? curPage;

	const setCurPageValue = setStep ?? setCurPage;

	const [currentCategory, setCurrentCategory] = useState<number>(0);
	const [liveSessionsNextConfirmationMessage, setLiveSessionsNextConfirmationMessage] = useState<string | undefined>();

	const totalPageIndexNumber = totalPageNames.length - 1;

	const modalBodyRef = useRef<HTMLDivElement>(null);
	const optionBodyRef = useRef<HTMLDivElement | null>(null);
	// whenever step changes, focus the modal body
	useEffect(() => {
		if (modalBodyRef.current) modalBodyRef.current.focus();
	}, [curPageValue]);

	// For configuration page
	useEffect(() => {
		if (optionBodyRef.current) optionBodyRef.current.focus();
	}, [currentCategory]);

	/**
	 * General Stepper function to pass into the new CoreStepper,
	 * which allows free backward navigation and free forward navigation to any completed step.
	 *
	 * Pass in the step to go to. This function will update necessary state, and do validation checks.
	 * More explanation on the configuration step inside the function.
	 */
	const navigateToStep = useCallback(
		(targetStep: number) => {
			const currentPageName = totalPageNames[curPageValue];

			/**
			 *  Currently passing targetStep === currentPageValue if on configuration tab.
			 *  Previously, did not do that and ran into errors when user would be on configuration tab, and clicked on a different step.
			 *    Instead of going to that step, the user would move up or down a catagory.
			 *    So now that targetStep === currentPageValue, need to be able to differentiate if user is going backwards or forwards, so
			 *    I passed in negative/postive targetStep. This is only for configuration step.
			 *    If anyone else has more elegant logic, feel free to change.
			 */
			if (currentPageName === "Configuration" && curPageValue === Math.abs(targetStep)) {
				const movingBackward = targetStep < 0;
				const withinCategoryBounds = movingBackward ? currentCategory > 0 : currentCategory < totalCategories - 1;

				if (withinCategoryBounds && !enforcedProfilesOnly) {
					setCurrentCategory((prev) => prev + (movingBackward ? -1 : 1));
				} else {
					setCurPageValue((prev) => prev + (movingBackward ? -1 : 1));
					if (!movingBackward) {
						setCompletedStepsSet((prev) => {
							const next = new Set(prev).add(currentPageName);
							return next;
						});
					}
				}
				return;
			}

			// Backward navigation
			if (targetStep < curPageValue) {
				setCurPageValue(() => Math.max(0, targetStep));
			}
			// Going forward
			else {
				// Live session check
				if (currentPageName === "Live Sessions" && !validateLiveSession()) {
					return;
				}
				// Normal Page advance
				setCurPageValue(() => Math.min(totalPageIndexNumber, targetStep));
				setCompletedStepsSet((prev) => {
					const next = new Set(prev).add(currentPageName);
					return next;
				});
			}
		},
		[
			curPageValue,
			totalPageNames,
			setCurPageValue,
			enforcedProfilesOnly,
			currentCategory,
			validateLiveSession,
			totalCategories,
			totalPageIndexNumber,
		],
	);

	const nextButtonHandler = useCallback(() => {
		// If on configuration, pass in curPage value to tell navigateToStep not to change the page, else pass in the next page
		navigateToStep(
			totalPageNames[curPageValue] === "Configuration" && !isMobileLayout ? curPageValue : curPageValue + 1,
		);
	}, [curPageValue, navigateToStep, totalPageNames]);
	const backButtonHandler = useCallback(() => {
		// If on configuration, pass in negative curPage value (to tell navigateToStep that we are going back), else pass in the previous page
		navigateToStep(
			totalPageNames[curPageValue] === "Configuration" && !isMobileLayout ? -curPageValue : curPageValue - 1,
		);
	}, [curPageValue, navigateToStep, totalPageNames]);
	const goToPage = (pageName: ConfigurationPageName) => {
		const index = totalPageNames.indexOf(pageName);
		if (index >= 0) {
			navigateToStep(index);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			saveButtonHandler,
			closeButtonHandler,
			saveAsProfileButtonHandler,
			saveAsProfile,
			nextButtonHandler,
			backButtonHandler,
			goToPage,
		}),
		[backButtonHandler, nextButtonHandler, saveButtonHandler, closeButtonHandler],
	);

	const standAloneExemptedStudentsContext = useContext(StandAloneExemptedStudentsContext);
	const isStandaloneQuizFlow =
		standAloneExemptedStudentsContext.studentList.length > 0 ||
		standAloneExemptedStudentsContext.exemptedStudents.length > 0;

	const [exemptionInfo, setExemptionInfo] = useState<{
		nonExemptRoster: Map<number, ExemptedStudent>;
		exemptRoster: Map<number, ExemptedStudent>;
	}>({
		nonExemptRoster: new Map(),
		exemptRoster: new Map(),
	});

	const [instituteUuid, setInstituteUuid] = useState<string | undefined>(undefined);
	const [courseId, setCourseId] = useState<number | undefined>(undefined);

	// if creating/ editing a standalone assessment, use the value from standAloneExemptedStudentsContext to set the default value for exemptionInfo

	// When creating a standalone assessment, the uploadedExemptedStudentsMap is set using setUploadedExemptedStudentsMap(exemptionInfo) in ExemptionSection.
	// uploadedExemptedStudentsMap is declared in CreateStandaloneAssessmentSection, and is used to create exemption emails for the standalone assessment.

	useEffect(() => {
		const nonExemptedStudentsMap = new Map<number, ExemptedStudent>();
		const exemptedStudentsMap = new Map<number, ExemptedStudent>();

		const { studentList, exemptedStudents } = standAloneExemptedStudentsContext;
		const studentListUserIds = new Set(studentList.map((student) => student.userId));

		const exemptedUserIds = new Set(exemptedStudents.map((student) => student.userId));

		const nonExemptedStudents = studentList.filter((student) => !exemptedUserIds.has(student.userId));

		// if this is false, the instructors added/removed students from the standalone student list. Update the exemptionInfo accordingly.
		const isStudentListEqualToStudentsInExemptionInfo =
			studentList.every(
				(student) =>
					exemptionInfo.exemptRoster.has(student.userId) || exemptionInfo.nonExemptRoster.has(student.userId),
			) &&
			Array.from(exemptionInfo.exemptRoster.values())
				.concat(Array.from(exemptionInfo.nonExemptRoster.values()))
				.every((student) => studentListUserIds.has(student.userId));

		if (
			isStandaloneQuizFlow &&
			(!isStudentListEqualToStudentsInExemptionInfo ||
				(exemptionInfo.exemptRoster.size === 0 && exemptionInfo.nonExemptRoster.size === 0))
		) {
			nonExemptedStudents.forEach((nonExemptedStudent) =>
				nonExemptedStudentsMap.set(nonExemptedStudent.userId, nonExemptedStudent),
			);
			exemptedStudents.forEach((exemptedStudent) => {
				exemptedStudentsMap.set(exemptedStudent.userId, exemptedStudent);
			});

			setExemptionInfo({
				nonExemptRoster: nonExemptedStudentsMap,
				exemptRoster: exemptedStudentsMap,
			});
		}
	}, [standAloneExemptedStudentsContext, isStandaloneQuizFlow, exemptionInfo]);

	const [filteredProfilesMap, setFilteredProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(new Map());

	async function getAssessmentUuids() {
		const quizId = quizInfo.quizId;

		let instituteId: number | undefined;
		try {
			instituteId = await getUserInstituteId();
		} catch (e) {
			void logger?.error(e);
		}
		let assessment: any | undefined = undefined;

		if (quizId) {
			assessment = await getAssessmentByAssessmentInfo({
				...quizInfo,
				quizId,
			});
		}

		let course: { accessControlUuid: string; instituteId: number };
		if (assessment) {
			course = await getCourseById(assessment.courseId);
		} else if (quizInfo.courseId) {
			course = await getCourseByLmsCourseId({
				lmsCourseId: quizInfo.courseId,
				instituteId,
			});
		} else {
			throw new Error("Course could not be found");
		}

		const institute = await getInstitute(course.instituteId);
		setInstituteUuid(institute.uuid);
		setCourseId(assessment?.courseId);
		assessmentUuids.current = {
			assessmentUuid: assessment ? intIdToUuid(assessment.id) : undefined,
			courseUuid: course.accessControlUuid,
			instituteUuid: institute.uuid,
		};
	}

	async function fetchProfiles(): Promise<Map<number, ProctoringOptionProfileV2>> {
		const tempInstituteSettings = await getVerityInstituteSettings();
		const profiles = await getProctoringProfiles({ lmsProvidedCourseId: quizInfo.courseId });

		const tempProfileMap = new Map(
			profiles
				.filter(
					(profile) =>
						(!enforcedProfilesOnly || profile.instituteProfile?.enforced) &&
						((profile.proctoringOption.isExtension && tempInstituteSettings.allowExtensionPlatform) ||
							(profile.proctoringOption.isLockdown && tempInstituteSettings.allowLockdownPlatform) ||
							(profile.proctoringOption.isMobile && tempInstituteSettings.allowMobilePlatform)),
				)
				.map((x) => [x.id, x]),
		);
		setFilteredProfilesMap(tempProfileMap);
		setProfiles(profiles);

		return tempProfileMap;
	}

	async function getEditAssessmentInfo() {
		setLoading(true);
		const tempInstituteSettings = await getVerityInstituteSettings();
		setVerityInstituteSettings(tempInstituteSettings);
		await getAssessmentUuids();
		if (assessmentUuids.current?.instituteUuid) {
			setOptionRelatedInstituteSettings(await getInstituteSettings(assessmentUuids.current.instituteUuid));
		}
		setEnabledProctoringOptions(await getEnabledProctoringOptions({ assessmentInfo: quizInfo, courseName }));

		setHasInstituteProfilePermissions(await doesUserHaveInstituteProfilePermissions());

		const tempProfileMap = await fetchProfiles();

		await fetchAndSetLiveSessions();

		try {
			// TODO: remove try catch once 500 issue resolved
			if (!isStandaloneQuizFlow)
				// setExemptionInfo again only if not a standalone assessment
				await toggleExemptionModal(tempProfileMap);
		} catch (e) {
			console.error(e);
		}
		setLoading(false);
	}

	useEffect(() => {
		void getEditAssessmentInfo();
	}, [enforcedProfilesOnly]);

	async function toggleExemptionModal(tempProfileMap: Map<number, ProctoringOptionProfileV2>) {
		const quizId = quizInfo.quizId;

		if (!quizInfo.courseId) {
			console.warn("Missing courseId; cannot load exemptions.");
			return;
		}
		try {
			const studentRoster = await getCourseStudents(quizInfo);
			let exemptedUserIds: Omit<ExemptedStudent, "name">[] = [];
			if (quizId) {
				exemptedUserIds = await getProctoringExemptionStudents({ ...quizInfo, quizId });
			} else if (uploadedExemptedStudentsMap) {
				exemptedUserIds = Array.from(uploadedExemptedStudentsMap.values()).map(({ name, ...rest }) => rest);
			}

			const exemptedStudentsMap = new Map(exemptedUserIds.map((e) => [e.userId, e]));

			// Categorize student roster by exemptions
			setExemptionInfo(
				(studentRoster ?? [])
					.map((s) => ({ userId: s.id, name: s.name }))
					.reduce(
						(prev, s) => {
							const exemptedStudent = exemptedStudentsMap.get(s.userId);
							if (exemptedStudent) {
								const studentProfileId = exemptedStudent.proctoringOptionProfileId;
								prev.exemptRoster.set(s.userId, {
									...s,
									type: exemptedStudent.type,
									accommodationNotes: exemptedStudent.accommodationNotes ?? undefined,
									proctoringOptionProfileId:
										studentProfileId && tempProfileMap.has(studentProfileId) ? studentProfileId : undefined,
								});
							} else {
								prev.nonExemptRoster.set(s.userId, s);
							}
							return prev;
						},
						{
							nonExemptRoster: new Map() as Map<number, ExemptedStudent>,
							exemptRoster: new Map() as Map<number, ExemptedStudent>,
						},
					),
			);
		} catch (err) {
			console.error(err);
			return false;
		}
	}

	const [hasActiveEnforcedProfile, setHasActiveEnforcedProfile] = useState(false);

	useEffect(() => {
		onBackEnabledChange?.(curPageValue > 0);
		setIsFinalPage?.(curPageValue === totalPageNames.length - 1);

		const shouldEnableNextButton = enforcedProfilesOnly
			? thirdPartySaveEnabled &&
				curPageValue < totalPageNames.length - 1 &&
				!(totalPageNames[curPageValue] === "Platform" && !hasActiveEnforcedProfile)
			: thirdPartySaveEnabled && curPageValue < totalPageNames.length - 1;

		onNextEnabledChange?.(shouldEnableNextButton);
	}, [curPageValue, totalPageNames.length, hasActiveEnforcedProfile, enforcedProfilesOnly, thirdPartySaveEnabled]);

	useEffect(() => {
		// Live session not okay if user chose live session option but did not create a live session
		const liveSessionOk = !proctoringAssessmentConfiguration?.isLiveProctored || pendingLiveSessionIds.length > 0;

		const saveEnabled =
			liveSessionOk &&
			thirdPartySaveEnabled &&
			(!enforcedProfilesOnly || Boolean(activeProfile)) &&
			isValidPlatform({
				option: proctoringOption,
				verityInstituteSettings: verityInstituteSettings,
			});
		onSaveEnabledChange?.(saveEnabled);
	}, [
		thirdPartySaveEnabled,
		onSaveEnabledChange,
		enforcedProfilesOnly,
		activeProfile,
		proctoringOption,
		verityInstituteSettings,
		proctoringAssessmentConfiguration?.isLiveProctored,
		pendingLiveSessionIds.length,
	]);

	useEffect(() => {
		async function fetchApplicationData() {
			const APPLICATION_LIST = await getAllApplications();
			setApplications(APPLICATION_LIST);
		}

		async function fetchExtensionData() {
			const EXTENSION_LIST = await getAllExtensions();
			setExtensions(EXTENSION_LIST);
		}
		async function fetchThirdPartyPlatforms() {
			const response = await getThirdPartyPlatforms();
			const platforms = Object.values(response.selectedPlatformRoster);
			platforms.push({ platformName: "Others", platformId: -1 });

			setThirdPartyPlatforms(platforms);
		}

		void fetchApplicationData();
		void fetchExtensionData();
		void fetchThirdPartyPlatforms();
	}, []);

	useEffect(() => {
		setExemptionCount(exemptionInfo.exemptRoster.size);
	}, [exemptionInfo]);

	useEffect(() => {
		if (
			url &&
			proctoringAssessmentConfiguration &&
			proctoringAssessmentConfiguration.thirdPartyAssessmentOption &&
			!proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.thirdPartyUrl
		) {
			setProctoringAssessmentConfiguration({
				...proctoringAssessmentConfiguration,
				thirdPartyAssessmentOption: {
					...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
					thirdPartyUrl: url,
				},
			});
		}
	}, []);

	const configurationPageIndex = totalPageNames.findIndex((page) => page === "Configuration");

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);

		window.addEventListener("resize", handleResize);

		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const stepper = isMobileLayout ? (
		<CorePaper component="section" className={styles.stepperContainer} sx={{ padding: "10px" }}>
			<div className={styles.stepperHeader}>{i18nPackage.t("editQuizPage.setupProctoring")}</div>
			<CoreStepper
				className={`${styles.headerStepper} ${styles.headerStepperCompact}`}
				activeStep={curPageValue}
				steps={totalPageNames}
				onStepChange={navigateToStep}
				noWrapLabel
				variant="scrollable"
				completedSteps={totalPageNames
					.map((name, i) => (completedStepsSet.has(name) ? i : null))
					.filter((i): i is number => i !== null)}
			/>
		</CorePaper>
	) : (
		<CoreStepper
			className={styles.headerStepper}
			activeStep={curPageValue}
			steps={totalPageNames}
			onStepChange={navigateToStep}
			completedSteps={totalPageNames
				.map((name, i) => (completedStepsSet.has(name) ? i : null))
				.filter((i): i is number => i !== null)}
		/>
	);

	if (proctoringAssessmentConfiguration && proctoringOption && verityInstituteSettings) {
		const setEnforcedProctoringOptions = (options: Partial<ProctoringOption>) =>
			setProctoringOption(
				enforceConfiguration(
					{ ...DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION, ...proctoringAssessmentConfiguration },
					options,
					verityInstituteSettings,
					optionRelatedInstituteSettings,
					enabledProctoringOptions,
				).proctoringOptions,
			);

		const loadProfile = (profile: ProctoringOptionProfileV2) => {
			setPreviousProfileOptions(proctoringOption);
			setEnforcedProctoringOptions({ ...proctoringOption, ...profile.proctoringOption });
			goToPage("Accommodations");
			setCompletedStepsSet(new Set(totalPageNames));
		};

		const clearProfile = (profile: ProctoringOptionProfileV2) => {
			setCurrentProfileId(undefined);
			if (
				// If the current proctoring options are the same as the cleared profile's options, reset proctoringOption to previousProfileOptions (if it exists)
				proctoringOption &&
				profile.proctoringOption &&
				previousProfileOptions &&
				equalProctoringOptions(convertPartialOptionsToNonNullable(proctoringOption), profile.proctoringOption)
			) {
				setProctoringOption(previousProfileOptions);
			}
		};

		return (
			<VerityApiContext.Provider value={api}>
				<I18nextProvider i18n={i18nPackage as i18n}>
					<form
						aria-label={i18nPackage.t("editQuizPage.formAriaLabel")}
						id="yuja-options-form"
						name="yuja-options-form"
						style={
							isMobileLayout
								? {
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										width: "100%",
										gap: "10px",
										background: "#f4f4f4",
										marginBottom: "10px",
									}
								: {
										minWidth: "max(275px, 100%)",
										width: "fit-content",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
									}
						}
					>
						{!totalSteps && stepper}

						<div
							style={{
								width: totalSteps && windowWidth > 1324 && curPageValue < configurationPageIndex ? "70%" : "100%",
								backgroundColor: isMobileLayout ? "#f4f4f4" : undefined,
							}}
							tabIndex={-1}
							ref={modalBodyRef}
							className={styles["modal-body"]}
						>
							{proctoringAssessmentConfiguration.thirdPartyAssessmentOption &&
								totalPageNames[curPageValue] === "Third-Party" && (
									<ThirdPartyStep
										isFirstTime={!initialProctoringAssessmentConfiguration?.enabled}
										thirdPartyPassword={proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.password}
										setThirdPartyPassword={(password) =>
											setProctoringAssessmentConfiguration({
												...proctoringAssessmentConfiguration,
												thirdPartyAssessmentOption: {
													...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
													password: password,
												},
											})
										}
										setThirdPartySaveEnabled={setThirdPartySaveEnabled}
										proctoringOption={proctoringOption}
										setProctoringOption={setProctoringOption}
										selectedPlatformId={
											proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyPlatformId
										}
										setSelectedPlatformId={(platformId) =>
											setProctoringAssessmentConfiguration({
												...proctoringAssessmentConfiguration,
												thirdPartyAssessmentOption: {
													...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
													thirdPartyPlatformId: platformId,
												},
											})
										}
										platforms={thirdPartyPlatforms}
									/>
								)}

							{totalPageNames[curPageValue] === "Platform" && (
								<PlatformStep
									option={proctoringOption}
									updateOption={setProctoringOption}
									verityInstituteSettings={verityInstituteSettings}
									disabled={enforcedProfilesOnly}
									profiles={profiles}
									refreshProfiles={fetchProfiles}
									activeProfileId={currentProfileId}
									setActiveProfileId={setCurrentProfileId}
									loadProfile={loadProfile}
									forceChromeOsManagedGuestSession={proctoringAssessmentConfiguration.forceChromeOsManagedGuestSession}
									clearProfile={clearProfile}
									optionRelatedInstituteSettings={optionRelatedInstituteSettings}
									lmsCourseId={quizInfo.courseId}
									hasInstituteProfilePermissions={hasInstituteProfilePermissions}
									enforcedProfilesOnly={enforcedProfilesOnly}
									loading={loading}
									setHasActiveEnforcedProfile={setHasActiveEnforcedProfile}
								/>
							)}

							{totalPageNames[curPageValue] === "Configuration" && (
								<ProctorSettings
									option={proctoringOption}
									updateOption={setProctoringOption}
									disabled={enforcedProfilesOnly}
									shouldShowOption={shouldShowOption}
									isOptionAllowed={isOptionAllowed}
									getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
									applications={applications}
									fetchChromeExtensionData={fetchChromeExtensionData}
									extensions={extensions}
									allowedCategories={allowedCategories}
									currentCategory={currentCategory}
									setCurrentCategory={setCurrentCategory}
									proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
									setOptionBodyRef={(el) => {
										optionBodyRef.current = el;
									}}
									instituteUuid={instituteUuid}
									getInstituteSettings={getInstituteSettings}
									courseId={courseId}
								/>
							)}

							{totalPageNames[curPageValue] === "Method" && (
								<MethodStep
									verityInstituteSettings={verityInstituteSettings}
									enabledProctoringOptions={enabledProctoringOptions}
									options={proctoringAssessmentConfiguration}
									updateOptions={setProctoringAssessmentConfiguration}
									hasUpcomingSessions={() => {
										const currTime = new Date();
										return Object.values(pendingLiveSessions).some(({ startTime }) => startTime > currTime);
									}}
									updateCompletedSteps={setCompletedStepsSet}
								/>
							)}

							{totalPageNames[curPageValue] === "Live Sessions" && (
								<LiveSessionsStep
									existingSessions={existingLiveSessions}
									pendingSessionIds={pendingLiveSessionIds}
									setPendingSessionIds={setPendingLiveSessionIds}
									pendingSessions={pendingLiveSessions}
									setPendingSessions={setPendingLiveSessions}
									availableProctors={availableProctors}
									availableProctorGroups={availableProctorGroups}
									getNextFakeLiveSessionId={getNextFakeLiveSessionId}
									nextConfirmationDialogMessage={liveSessionsNextConfirmationMessage}
									cancelNextConfirmationDialog={() => setLiveSessionsNextConfirmationMessage(undefined)}
									confirmNextConfirmationDialog={() => {
										setLiveSessionsNextConfirmationMessage(undefined);
										if (totalPageNames[curPageValue] === "Live Sessions") {
											navigateToStep(curPageValue + 1);
										}
									}}
									allowDemoSessions={verityInstituteSettings.allowDemoMode}
								/>
							)}

							{totalPageNames[curPageValue] === "Accommodations" && (
								<ModerateTheQuiz
									quizInfo={quizInfo}
									profiles={profiles}
									refreshProfiles={fetchProfiles}
									hasInstituteProfilePermissions={hasInstituteProfilePermissions}
									optionRelatedInstituteSettings={optionRelatedInstituteSettings}
									verityInstituteSettings={verityInstituteSettings}
									enforcedProfilesOnly={enforcedProfilesOnly}
									setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
									setUploadedProfilesMap={setUploadedProfilesMap}
									exemptionInfo={exemptionInfo}
									setExemptionInfo={setExemptionInfo}
									filteredProfilesMap={filteredProfilesMap}
									setStudentsLoaded={setStudentsLoaded}
								/>
							)}

							{totalPageNames[curPageValue] === "Confirmation" && (
								<ConfirmationPage
									name={quizName}
									proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
									option={proctoringOption}
									quizInfo={quizInfo}
									exemptionCount={exemptionCount}
									pendingLiveSessions={pendingLiveSessionIds.map((id) => pendingLiveSessions[id])}
									loading={loading}
									setCurPageValue={setCurPageValue}
									enforcedProfilesOnly={enforcedProfilesOnly}
									verityInstituteSettings={verityInstituteSettings}
									optionRelatedInstituteSettings={optionRelatedInstituteSettings}
									totalPageNames={totalPageNames}
									isStandaloneQuizFlow={isStandaloneQuizFlow}
									applications={applications}
									extensions={extensions}
									instituteUuid={instituteUuid}
									getInstituteSettings={getInstituteSettings}
									setThirdPartySaveEnabled={setThirdPartySaveEnabled}
									setSelectedPlatformId={(platformId) =>
										setProctoringAssessmentConfiguration({
											...proctoringAssessmentConfiguration,
											thirdPartyAssessmentOption: {
												...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
												thirdPartyPlatformId: platformId,
											},
										})
									}
								/>
							)}

							{!isStandaloneQuizFlow &&
								!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption &&
								totalPageNames[curPageValue] === "Confirmation" &&
								openDisclaimer && (
									<DisclaimerModal
										open={openDisclaimer}
										themeExists={themeExists}
										verifyingTheme={verifyingTheme}
										setFinishedPresave={setFinishedPresave}
										setOpen={setOpenDisclaimer}
										setCallReset={setCallReset}
									/>
								)}

							{saveProfileModalShowing && (
								<SaveProfile
									proctoringOption={proctoringOption}
									activeProfileId={activeProfile?.id}
									setActiveProfileId={setCurrentProfileId}
									refreshProfiles={fetchProfiles}
									profiles={profiles ?? []}
									setIsUpdateCurrentProfileClicked={setIsUpdateCurrentProfileClicked}
									open={saveProfileModalShowing}
									onClose={() => setSaveProfileModalShowing(false)}
									lmsCourseId={quizInfo.courseId}
									hasInstituteProfilePermissions={hasInstituteProfilePermissions}
									setProctoringOption={setProctoringOption}
									optionRelatedInstituteSettings={optionRelatedInstituteSettings}
									verityInstituteSettings={verityInstituteSettings}
								/>
							)}

							{saveConflictModalShowing && activeProfile && (
								<SaveConflict
									open={saveConflictModalShowing}
									onClose={() => setSaveConflictModalShowing(false)}
									profile={activeProfile}
									hasInstituteProfilePermissions={hasInstituteProfilePermissions}
									applyProfile={() => {
										onStartSaveOptions?.();
										setProctoringOption(activeProfile.proctoringOption);
										saveProctoringConfiguration(password, additionalAssessmentInfo, true)
											.then((options) => optionsSaved(true, options))
											.catch(() => optionsSaved(false, undefined, "Failed to apply profile."));
									}}
									applyOptions={() => {
										onStartSaveOptions?.();
										setCurrentProfileId(undefined);
										saveProctoringConfiguration(password, additionalAssessmentInfo, false, true)
											.then((options) => optionsSaved(true, options))
											.catch(() => optionsSaved(false, undefined, "Failed to save proctoring options."));
									}}
									updateAndApplyProfile={() => {
										onStartSaveOptions?.();
										updateProfile()
											.catch(() => optionsSaved(false, undefined, "Failed to update profile options."))
											.then(() => saveProctoringConfiguration(password, additionalAssessmentInfo))
											.catch(() => optionsSaved(false, undefined, "Failed to apply profile."))
											.then((options) => optionsSaved(true, options ?? undefined))
											.catch(() => optionsSaved(false));
									}}
								/>
							)}
							{unsavedChangesModalOpen && (
								<UnsavedChangesModal
									openModal={unsavedChangesModalOpen}
									onClose={() => {
										confirmCloseModalResolve?.(false);
										setUnsavedChangesModalOpen(false);
									}}
									onConfirm={() => confirmCloseModalResolve?.(true)}
								/>
							)}

							{setNoConfigurationsEnabledModalOpen && (
								<NoConfigurationsEnabledModal
									openModal={noConfigurationsEnabledModalOpen}
									setOpenModal={setNoConfigurationsEnabledModalOpen}
									onClose={() => {
										confirmSaveModalResolve?.(false);
									}}
									onConfirm={() => {
										confirmSaveModalResolve?.(true);
									}}
								/>
							)}
						</div>
					</form>
					<CoreToastContainer containerId="editQuiz" enableMultiContainer />
				</I18nextProvider>
			</VerityApiContext.Provider>
		);
	} else {
		return null;
	}
});
